import React, { useEffect, useState } from "react";
import knowThyselfVideo from "../../assets/onboarding/KnowThyself.mp4";
import mountainFallback from "../../assets/onboarding/OnboardingMountain.png";
import { readinessCueAt } from "./onboardingCopy";

interface OnboardingVideoBackdropProps {
  /** Receives the video clock (seconds) so the readiness tag can follow it. */
  onTime?: (seconds: number) => void;
  /** Extra blur/dim for use behind text (welcome). Lighter for the side panel. */
  intensity?: "welcome" | "panel";
}

/**
 * Muted, looping Know Thyself video (iOS `OnboardingVideoBackground`), blurred
 * with a frosted + dark overlay. Falls back to the mountain image on error.
 */
export const OnboardingVideoBackdrop: React.FC<OnboardingVideoBackdropProps> = ({ onTime, intensity = "welcome" }) => {
  const [failed, setFailed] = useState(false);
  const media = "absolute left-1/2 top-1/2 h-[112%] w-[112%] -translate-x-1/2 -translate-y-1/2 object-cover blur-2xl";
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-black" aria-hidden="true">
      {!failed ? (
        <video
          className={media}
          src={knowThyselfVideo}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onTimeUpdate={(e) => onTime?.(e.currentTarget.currentTime)}
          onError={() => setFailed(true)}
        />
      ) : (
        <img src={mountainFallback} alt="" className={media} />
      )}
      <div className={`absolute inset-0 backdrop-blur-sm ${intensity === "welcome" ? "bg-white/[0.08]" : "bg-white/[0.04]"}`} />
      <div className={`absolute inset-0 ${intensity === "welcome" ? "bg-black/[0.22]" : "bg-black/[0.32]"}`} />
    </div>
  );
};

/** Geist Mono 12 tag, typewriter-typed, colour + label keyed to the video clock. */
export const ReadinessTag: React.FC<{ seconds: number; className?: string }> = ({ seconds, className = "" }) => {
  const cue = readinessCueAt(seconds);
  const [typed, setTyped] = useState("");
  useEffect(() => {
    setTyped("");
    let index = 0;
    const timer = setInterval(() => {
      index += 1;
      setTyped(cue.label.slice(0, index));
      if (index >= cue.label.length) clearInterval(timer);
    }, 28);
    return () => clearInterval(timer);
  }, [cue.label]);
  return (
    <p className={`min-h-[16px] font-geist text-[12px] font-medium ${className}`} style={{ color: cue.color }} aria-label={cue.label}>
      {typed}
    </p>
  );
};
